import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Banknote, CircleDollarSign, CreditCard, FileText, Landmark, List, RefreshCw, UserRound, WalletCards } from 'lucide-react';
import { adminAccountsApi } from '../../../api/admin/accounts';
import {
  adminPaymentsApi,
  type PaymentHistoryFilter,
  type PaymentHistoryQuery,
  type PaymentHistoryRow,
  type PaymentOperation,
  type PaymentType,
} from '../../../api/admin/payments';
import { Loader } from '../../../components/ui/Loader';
import type { AdminEditableAccount } from '../../../types/admin-account';

const money = (value: string | number | null | undefined) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? Math.trunc(number).toLocaleString('en-US') : '0';
};

const summarySize = (value: string | number | null | undefined) => {
  const length = money(value).length;
  if (length >= 12) return 'text-[10px] sm:text-sm';
  if (length >= 9) return 'text-[11px] sm:text-base';
  if (length >= 7) return 'text-xs sm:text-lg';
  return 'text-sm sm:text-xl';
};

const uplineTone = (value: string | number | null | undefined) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number) || number === 0) return 'admin-upline-neutral';
  return number < 0 ? 'admin-upline-negative' : 'admin-upline-positive';
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const filterIcon: Record<PaymentHistoryFilter, typeof List> = { DRAW_ACCOUNT: WalletCards, MANUAL_ACCOUNT: Landmark, DIRECT_ACCOUNT: UserRound, ALL: List, CREDIT: CreditCard, CASH: Banknote };

const filterButtons: Array<{ value: PaymentHistoryFilter; label: string; activeClass: string }> = [
  { value: 'DRAW_ACCOUNT', label: 'Draw کا حساب', activeClass: 'crystal-ruby' },
  { value: 'MANUAL_ACCOUNT', label: 'Manual حساب', activeClass: 'crystal-emerald' },
  { value: 'DIRECT_ACCOUNT', label: 'This User Account', activeClass: 'crystal-sapphire' },
  { value: 'ALL', label: 'All', activeClass: 'crystal-smoky' },
  { value: 'CREDIT', label: 'Credit', activeClass: 'crystal-violet' },
  { value: 'CASH', label: 'Cash', activeClass: 'crystal-sapphire' },
];

const makeHistoryQuery = (type: PaymentHistoryFilter, start: string, end: string, search: string, page: number): PaymentHistoryQuery => ({
  type,
  page,
  pageSize: 50,
  ...(start ? { start } : {}),
  ...(end ? { end } : {}),
  ...(search.trim() ? { search: search.trim() } : {}),
});

function visiblePages(page: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const items: Array<number | 'ellipsis'> = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) items.push('ellipsis');
  for (let current = start; current <= end; current += 1) items.push(current);
  if (end < totalPages - 1) items.push('ellipsis');
  items.push(totalPages);
  return items;
}

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `admin-payment:${crypto.randomUUID()}`;
  return `admin-payment:${Date.now()}:${Math.random().toString(36).slice(2, 14)}`;
}

export default function AccountPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState<AdminEditableAccount | null>(null);
  const [transactions, setTransactions] = useState<PaymentHistoryRow[]>([]);
  const [paymentType, setPaymentType] = useState<PaymentType>('CREDIT');
  const [historyType, setHistoryType] = useState<PaymentHistoryFilter>('ALL');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setHistoryPage(1);
    }, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [search]);

  const historyQuery = useMemo(
    () => makeHistoryQuery(historyType, startDate, endDate, debouncedSearch, historyPage),
    [debouncedSearch, endDate, historyPage, historyType, startDate],
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setHistoryLoading(true);
    setError('');
    void Promise.all([
      adminAccountsApi.getAccount(id),
      adminPaymentsApi.history(id, historyQuery),
    ])
      .then(([accountResponse, historyResponse]) => {
        if (cancelled) return;
        setAccount(accountResponse.account);
        setTransactions(historyResponse.transactions);
        setHistoryPagination(historyResponse.pagination);
        if (historyResponse.pagination.page !== historyPage) setHistoryPage(historyResponse.pagination.page);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Payment data load failed');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHistoryLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [historyQuery, id]);

  const selectedBucket = useMemo(() => {
    if (!account?.wallet) return '0';
    return paymentType === 'CASH' ? account.wallet.cashUpline : account.wallet.creditBalance;
  }, [account, paymentType]);

  const reloadCurrent = async () => {
    if (!id) return;
    const [accountResponse, historyResponse] = await Promise.all([
      adminAccountsApi.getAccount(id),
      adminPaymentsApi.history(id, historyQuery),
    ]);
    setAccount(accountResponse.account);
    setTransactions(historyResponse.transactions);
    setHistoryPagination(historyResponse.pagination);
    if (historyResponse.pagination.page !== historyPage) setHistoryPage(historyResponse.pagination.page);
  };

  const submit = async (operation: PaymentOperation) => {
    if (!id || saving) return;
    const normalized = amount.trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(normalized) || Number(normalized) <= 0) {
      setError('درست رقم درج کریں۔');
      return;
    }
    if (description.length > 1000) {
      setError('Description زیادہ سے زیادہ 1000 حروف ہو سکتی ہے۔');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const note = description.trim();
      const result = await adminPaymentsApi.transfer(id, {
        paymentType,
        operation,
        amount: normalized,
        idempotencyKey: newIdempotencyKey(),
        ...(note ? { description: note } : {}),
      });
      setSuccess(result.created ? 'Payment successfully recorded.' : 'This payment was already processed safely.');
      setAmount('');
      setDescription('');
      await reloadCurrent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setSaving(false);
    }
  };

  const resetHistoryFilters = () => {
    setHistoryType('ALL');
    setHistoryPage(1);
    setSearch('');
    setDebouncedSearch('');
    setStartDate('');
    setEndDate('');
  };

  const openPdf = () => {
    if (!id) return;
    window.open(adminPaymentsApi.historyPdfUrl(id, historyQuery), '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="grid min-h-[45vh] place-items-center"><Loader label="Loading payment account…" /></div>;
  if (!account) return <section className="rounded-2xl border p-5"><p className="font-bold">Account unavailable</p><p className="mt-2 text-sm text-slate-400">{error || 'Unable to load account.'}</p></section>;

  const pages = visiblePages(historyPagination.page, historyPagination.totalPages);

  return (
    <section className="overflow-hidden rounded-[1.5rem] border bg-[rgba(18,26,43,0.85)] shadow-[0_18px_42px_rgba(0,0,0,0.28)] backdrop-blur-md">
      <div className="admin-border border-b p-4 sm:p-5">
        <button type="button" onClick={() => navigate('/')} className="crystal-btn crystal-sapphire inline-flex min-h-10 items-center gap-2 rounded-xl px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white"><ArrowLeft size={15} aria-hidden="true" /><span>Back to Dashboard</span></button>
        <p className="mt-2 text-[10px] font-black uppercase tracking-[0.16em] text-sky-300">Account Payment</p>
        <h1 className="admin-text-strong mt-1 text-xl font-black">{account.displayName || account.username}</h1>
        <p className="admin-text-muted text-sm">@{account.username} · {account.role.replaceAll('_', ' ')}</p>
      </div>

      <div className="space-y-4 p-3 sm:p-5">
        <div className="grid grid-cols-3 gap-2">
          <div className="admin-subpanel min-w-0 overflow-hidden rounded-2xl p-2.5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,.14),0_8px_18px_rgba(0,0,0,.12)] sm:p-3">
            <p className="admin-text-muted text-[8px] font-black uppercase tracking-[0.08em] sm:text-[10px]">Upline</p>
            <p className={`numeric-data mt-2 whitespace-nowrap font-black leading-none tracking-tight ${uplineTone(account.wallet?.cashUpline)} ${summarySize(account.wallet?.cashUpline)}`}>{money(account.wallet?.cashUpline)}</p>
          </div>
          <div className="admin-subpanel min-w-0 overflow-hidden rounded-2xl p-2.5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,.14),0_8px_18px_rgba(0,0,0,.12)] sm:p-3">
            <p className="admin-text-muted text-[8px] font-black uppercase tracking-[0.08em] sm:text-[10px]">Credit</p>
            <p className={`admin-text-strong numeric-data mt-2 whitespace-nowrap font-black leading-none tracking-tight ${summarySize(account.wallet?.creditBalance)}`}>{money(account.wallet?.creditBalance)}</p>
          </div>
          <div className="admin-subpanel min-w-0 overflow-hidden rounded-2xl p-2.5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,.14),0_8px_18px_rgba(0,0,0,.12)] sm:p-3">
            <p className="admin-text-muted text-[8px] font-black uppercase tracking-[0.08em] sm:text-[10px]">Available</p>
            <p className={`admin-balance-text numeric-data mt-2 whitespace-nowrap font-black leading-none tracking-tight ${summarySize(account.wallet?.availableBalance)}`}>{money(account.wallet?.availableBalance)}</p>
          </div>
        </div>

        <div className="admin-subpanel rounded-2xl p-3 sm:p-4">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setPaymentType('CASH')} className={`crystal-btn rounded-xl px-3 py-3 text-sm font-black ${paymentType === 'CASH' ? 'crystal-sapphire' : 'crystal-smoky'}`}><span className="inline-flex items-center justify-center gap-1.5"><Banknote size={15} aria-hidden="true" />Cash</span></button>
            <button type="button" onClick={() => setPaymentType('CREDIT')} className={`crystal-btn rounded-xl px-3 py-3 text-sm font-black ${paymentType === 'CREDIT' ? 'crystal-violet' : 'crystal-smoky'}`}><span className="inline-flex items-center justify-center gap-1.5"><CreditCard size={15} aria-hidden="true" />Credit</span></button>
          </div>

          <label className="admin-data-secondary mt-4 grid gap-1.5 text-sm font-bold">Description (optional)
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} maxLength={1000} className="w-full rounded-xl border px-3 py-2 text-sm outline-none" placeholder="Payment note" />
          </label>

          <label className="admin-data-secondary mt-3 grid gap-1.5 text-sm font-bold">Amount
            <input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0" className="min-h-11 w-full rounded-xl border px-3 py-2 text-base font-black outline-none" />
          </label>
          <p className="admin-text-muted mt-1 text-[10px]">Selected {paymentType === 'CASH' ? 'Upline' : 'Credit'} balance: <span className="numeric-data font-black">{money(selectedBucket)}</span></p>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" disabled={saving} onClick={() => void submit('DEPOSIT')} className={`crystal-btn rounded-xl px-3 py-3 text-xs font-black disabled:opacity-50 sm:text-sm ${paymentType === 'CREDIT' ? 'crystal-violet' : 'crystal-sapphire'}`}><span className="inline-flex items-center justify-center gap-1.5"><CircleDollarSign size={15} aria-hidden="true" />{saving ? 'Processing…' : `Add ${paymentType === 'CASH' ? 'Cash' : 'Credit'}`}</span></button>
            <button type="button" disabled={saving} onClick={() => void submit('WITHDRAW')} className="crystal-btn crystal-ruby rounded-xl px-3 py-3 text-xs font-black disabled:opacity-50 sm:text-sm"><span className="inline-flex items-center justify-center gap-1.5"><Landmark size={15} aria-hidden="true" />{saving ? 'Processing…' : `Withdraw ${paymentType === 'CASH' ? 'Cash' : 'Credit'}`}</span></button>
          </div>

          {error ? <div role="alert" className="mt-3 rounded-xl border border-rose-400/25 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200">{error}</div> : null}
          {success ? <div className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-200">{success}</div> : null}
        </div>

        <div className="admin-subpanel overflow-hidden rounded-2xl">
          <div className="admin-border border-b px-3 py-3 sm:px-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div><p className="admin-text-muted text-[9px] font-black uppercase tracking-[0.14em]">Book Details</p><h2 className="admin-text-strong mt-0.5 text-base font-black">Transaction History</h2><p className="admin-text-muted mt-0.5 text-[10px]">Filter and review account activity</p></div>
              {historyLoading ? <span className="admin-text-muted text-[10px] font-bold">Filtering…</span> : null}
            </div>

            <div className="mt-3 space-y-2">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {filterButtons.slice(0, 3).map((filter) => (
                  <button key={filter.value} type="button" onClick={() => { setHistoryType(filter.value); setHistoryPage(1); }} className={`crystal-btn ${filter.activeClass} min-h-11 rounded-xl px-3 py-2.5 text-[10px] font-black shadow-[0_7px_16px_rgba(0,0,0,.18)] sm:text-xs ${historyType === filter.value ? 'ring-2 ring-white/70' : 'opacity-85'}`}>{(() => { const Icon = filterIcon[filter.value]; return <span className="inline-flex items-center justify-center gap-1.5"><Icon size={14} aria-hidden="true" />{filter.label}</span>; })()}</button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {filterButtons.slice(3).map((filter) => (
                  <button key={filter.value} type="button" onClick={() => { setHistoryType(filter.value); setHistoryPage(1); }} className={`crystal-btn ${filter.activeClass} min-h-10 rounded-xl px-2 py-2 text-[10px] font-black shadow-[0_6px_14px_rgba(0,0,0,.16)] sm:text-xs ${historyType === filter.value ? 'ring-2 ring-white/70' : 'opacity-85'}`}>{(() => { const Icon = filterIcon[filter.value]; return <span className="inline-flex items-center justify-center gap-1.5"><Icon size={13} aria-hidden="true" />{filter.label}</span>; })()}</button>
                ))}
              </div>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, amount or description" className="crystal-search min-h-10 w-full rounded-xl border px-3 py-2 text-sm font-semibold outline-none" />
              <div className="grid grid-cols-2 gap-2">
                <label className="admin-text-muted grid gap-1.5 text-[9px] font-black uppercase tracking-wide">Start Date<input type="date" value={startDate} max={endDate || undefined} onChange={(event) => { setStartDate(event.target.value); setHistoryPage(1); }} className="min-h-12 rounded-2xl border px-3 py-3 text-sm font-black outline-none shadow-[inset_0_1px_0_rgba(255,255,255,.10)]" /></label>
                <label className="admin-text-muted grid gap-1.5 text-[9px] font-black uppercase tracking-wide">End Date<input type="date" value={endDate} min={startDate || undefined} onChange={(event) => { setEndDate(event.target.value); setHistoryPage(1); }} className="min-h-12 rounded-2xl border px-3 py-3 text-sm font-black outline-none shadow-[inset_0_1px_0_rgba(255,255,255,.10)]" /></label>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:max-w-sm">
              <button type="button" onClick={resetHistoryFilters} className="crystal-btn crystal-smoky inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-black"><RefreshCw size={13} aria-hidden="true" />Reset</button>
              <button type="button" onClick={openPdf} className="crystal-btn crystal-violet inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-black"><FileText size={13} aria-hidden="true" />PDF Report</button>
            </div>
          </div>

          <div className="md:hidden">
            {transactions.length ? (
              <div className="divide-y divide-white/[0.08]">
                {transactions.map((row) => (
                  <article key={row.id} className="p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="admin-text-muted text-[9px] font-black uppercase tracking-[0.12em]">{formatDateTime(row.createdAt)}</p>
                        <p className="admin-text-strong mt-1 text-sm font-black">{row.paymentType}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wide ${row.operation === 'WITHDRAW' ? 'bg-rose-500/10 text-rose-300 ring-1 ring-rose-400/25' : 'bg-sky-500/10 text-sky-300 ring-1 ring-sky-400/25'}`}>
                        {row.operation === 'DEPOSIT' ? 'Add' : 'Withdraw'}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div className="admin-subpanel rounded-xl p-2.5">
                        <p className="admin-text-muted text-[8px] font-black uppercase tracking-wide">Amount</p>
                        <p className="admin-text-strong numeric-data mt-1 text-sm font-black">{money(row.amount)}</p>
                      </div>
                      <div className="admin-subpanel rounded-xl p-2.5">
                        <p className="admin-text-muted text-[8px] font-black uppercase tracking-wide">Upline</p>
                        <p className={`numeric-data mt-1 text-sm font-black ${uplineTone(row.upline)}`}>{money(row.upline)}</p>
                      </div>
                      <div className="admin-subpanel rounded-xl p-2.5">
                        <p className="admin-text-muted text-[8px] font-black uppercase tracking-wide">Balance</p>
                        <p className="admin-balance-text numeric-data mt-1 text-sm font-black">{money(row.balance)}</p>
                      </div>
                    </div>

                    {row.description ? (
                      <div className="mt-3 rounded-xl border border-white/[0.08] px-3 py-2">
                        <p className="admin-text-muted text-[8px] font-black uppercase tracking-wide">Description</p>
                        <p className="admin-text mt-1 break-words text-xs font-semibold">{row.description}</p>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : <p className="admin-text-muted p-5 text-center text-sm">No transactions match the selected filters.</p>}
          </div>

          <div className="hidden max-h-[30rem] overflow-auto [-webkit-overflow-scrolling:touch] md:block">
            <table className="w-max min-w-full border-collapse text-[10px] sm:text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="admin-border border-b">
                  <th className="whitespace-nowrap px-3 py-2 text-right font-black uppercase tracking-wide">Amount</th>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-black uppercase tracking-wide">Upline</th>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-black uppercase tracking-wide">Balance</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-black uppercase tracking-wide">Type</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-black uppercase tracking-wide">Action</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-black uppercase tracking-wide">Date</th>
                  <th className="min-w-[10rem] px-3 py-2 text-left font-black uppercase tracking-wide">Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length ? transactions.map((row) => (
                  <tr key={row.id} className="admin-border border-b last:border-b-0">
                    <td className="admin-text-strong numeric-data whitespace-nowrap px-3 py-2.5 text-right font-black">{money(row.amount)}</td>
                    <td className={`numeric-data whitespace-nowrap px-3 py-2.5 text-right font-black ${uplineTone(row.upline)}`}>{money(row.upline)}</td>
                    <td className="admin-balance-text numeric-data whitespace-nowrap px-3 py-2.5 text-right font-black">{money(row.balance)}</td>
                    <td className="admin-text-strong whitespace-nowrap px-3 py-2.5 font-black">{row.paymentType}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-black"><span className={row.operation === 'WITHDRAW' ? 'text-rose-400' : 'text-sky-300'}>{row.operation === 'DEPOSIT' ? 'ADD' : 'WITHDRAW'}</span></td>
                    <td className="admin-text-muted whitespace-nowrap px-3 py-2.5 font-semibold">{formatDateTime(row.createdAt)}</td>
                    <td className="admin-text-muted max-w-[18rem] px-3 py-2.5">{row.description || '—'}</td>
                  </tr>
                )) : <tr><td colSpan={7} className="admin-text-muted p-5 text-center text-sm">No transactions match the selected filters.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="admin-border flex flex-wrap items-center justify-between gap-2 border-t px-3 py-3">
            <p className="admin-text-muted text-[10px] font-semibold">{historyPagination.total.toLocaleString('en-US')} entries · 50 per page</p>
            <nav className="flex items-center gap-1" aria-label="Payment ledger pages">
              <button type="button" disabled={historyPagination.page <= 1} onClick={() => setHistoryPage(historyPagination.page - 1)} className="crystal-btn crystal-smoky min-w-8 rounded-lg px-2 py-1.5 text-[10px] font-black disabled:opacity-35">‹</button>
              {pages.map((item, index) => item === 'ellipsis'
                ? <span key={`ellipsis-${index}`} className="admin-text-muted px-1 text-xs">…</span>
                : <button key={item} type="button" onClick={() => setHistoryPage(item)} className={`crystal-btn min-w-8 rounded-lg px-2 py-1.5 text-[10px] font-black ${item === historyPagination.page ? 'crystal-violet' : 'crystal-smoky'}`}>{item}</button>)}
              <button type="button" disabled={historyPagination.page >= historyPagination.totalPages} onClick={() => setHistoryPage(historyPagination.page + 1)} className="crystal-btn crystal-smoky min-w-8 rounded-lg px-2 py-1.5 text-[10px] font-black disabled:opacity-35">›</button>
            </nav>
          </div>
        </div>
      </div>
    </section>
  );
}
